/**
 * @file receipt-print-items.ts
 * @description Строки диалога печати из проведённой приёмки (phase19.22,
 *   W17-5a; уточнение пользователя к ui-design §1: печать групповая, все
 *   строки документа, количество = принято, порядок строк документа).
 *
 * Статус этикеток берём из блока статуса (свежее, чем слепок в документе),
 * иначе — из партии строки. Строки без партии (приёмки до технической
 * партии) напечатать нельзя — сервер ответит `lot_required`, поэтому их нет.
 */

import type { LabelPrintDialogItem } from "./label-print-types";
import type { LotLabelStatus, ReceiptLabelStatusDto } from "../../../lib/api-labels";
import { formatSkuName, type GoodsReceiptDto } from "../../../lib/api-wms";
import { defaultLabelQty } from "../../../lib/label-sheets";

export function receiptPrintItems(
  receipt: GoodsReceiptDto,
  status: ReceiptLabelStatusDto | null,
): LabelPrintDialogItem[] {
  const byItem = new Map((status?.items ?? []).map((row) => [row.receiptItemId, row]));
  return (receipt.items ?? [])
    .filter((it) => !!it.lotId)
    .map((it) => {
      const row = byItem.get(it.id);
      return {
        skuId: it.skuId,
        skuName: formatSkuName(it.sku),
        skuCode: it.sku?.code ?? "",
        lotId: it.lotId,
        lotCode: it.lot?.code ?? row?.lotCode ?? undefined,
        batchNumber: it.lot?.batchNumber ?? it.batchNumber ?? undefined,
        expiresAt: it.lot?.expiresAt ?? it.expiresAt,
        receiptItemId: it.id,
        lineNo: it.lineNo ?? row?.lineNo,
        defaultQty: defaultLabelQty({ quantity: it.quantity, isMeasured: it.sku?.isMeasured }),
        isMeasured: it.sku?.isMeasured,
        // Шаблон — из описания печати строки (сервер), без запроса на фасовку.
        template: it.sku?.effectiveLabelTemplate,
        labelStatus: row?.labelStatus ?? it.lot?.labelStatus ?? null,
      };
    });
}

/** Сколько позиций ждут печати: не печатались + устарели. */
export function pendingLabelItems(status: ReceiptLabelStatusDto | null): number {
  return status ? status.notPrintedItems + status.staleItems : 0;
}

/** Этикетки строки приёмки по блоку статуса: колонка «Этикетки» и бейджи. */
export function receiptLabelOf(
  status: ReceiptLabelStatusDto | null,
): ((item: { id: string }) => { status: LotLabelStatus | null; printedAt: string | null } | undefined) | undefined {
  if (!status) return undefined;
  const byItem = new Map(status.items.map((row) => [row.receiptItemId, row]));
  return (item) => {
    const row = byItem.get(item.id);
    return row ? { status: row.labelStatus, printedAt: row.labelsPrintedAt } : undefined;
  };
}
